import { SEARCH_LONG_RUNNING_TASKS } from "adapters/ehr.service";
import { addTask, updateTask } from "../actions/BackgroundTaskActions";
import { toast } from "react-toastify";
import {
  calculateProgress,
  getTaskMessage,
  TaskProcessorRegistry,
  TASK_STATUS,
  extractTemplateName,
  extractSOAPNoteTitle,
} from "config/helpers/backgroundTaskUtils/backgroundTaskUtils";
import { getUnixMs } from "config/helpers/date_helper";
import { updateProgressState, resetProgressState } from "../actions/recognizerActions";
import { handleSOAPTaskCompletion } from "config/helpers/handleSOAPTaskCompletion";
import { extractPatientAndEncounterIdFromComposition } from "config/helpers/utils";
import { constants } from "../constants/redux-constants";
import { removeActiveSOAPGeneration } from "../actions/activeSOAPGenerationActions";
import { refreshPatientHistoryWindow } from "../actions/recognizerActions";

const POLL_INTERVAL = 2000;
const MAX_IDLE_MINUTES = 10;
const TASK_QUERY = "Task?code=http://enliv.com/fhir/coding/task/code/process-code|long-running-operation";

// Map of taskId -> { intervalId, taskCategory, addedOn, setRecognitionEvents }
const watchedTasks = new Map();

export const startWatchingTask = (taskId, taskCategory = "generic", setRecognitionEvents) => async (dispatch, getState) => {
  const PractitionerID = getState().auth?.practitionerData?.id;
  const patientName = getState().patientObj?.patientObj?.name?.[0]?.text || "";
  const patientId = getState().patientObj?.patientObj?.id || "";
  const encounterId = getState().encounterObj?.encounter?.id;

  if (!taskId || !TaskProcessorRegistry[taskCategory]) {
    console.error(
      `Cannot watch task: Invalid taskId or unknown category "${taskCategory}"`
    );
    return;
  }

  // If already watching this task, don't start another poller
  if (watchedTasks.has(taskId)) {
    console.log(`Task ${taskId} is already being watched`);
    return;
  }

  const existingTask = getState().backgroundTasks?.tasks?.find(
    (t) => t.id === taskId
  );
  const processor = TaskProcessorRegistry[taskCategory];

  if (!existingTask) {
    const initialState = processor.getInitialState(taskId);
    dispatch(
      addTask({
        ...initialState,
        practitionerId: PractitionerID,
        type: "background_task",
        task_category: taskCategory,
        addedOn: getUnixMs(),
        lastUpdated: Date.now(),
        patientName,
        patientId,
        encounterId,
      })
    );
  }

  // Start individual poller for this task
  const intervalId = startTaskPoller(dispatch, getState, taskId, taskCategory, setRecognitionEvents);

  watchedTasks.set(taskId, {
    intervalId,
    taskCategory,
    addedOn: Date.now(),
    setRecognitionEvents:
      typeof setRecognitionEvents === "function"
        ? setRecognitionEvents
        : () => { },
  });
};

export const stopWatchingTask = (taskId) => (dispatch) => {
  const taskData = watchedTasks.get(taskId);
  if (taskData && taskData.intervalId) {
    clearInterval(taskData.intervalId);
  }
  watchedTasks.delete(taskId);
};

export const stopAllPollingTasks = () => (dispatch) => {
  for (const [taskId, taskData] of watchedTasks.entries()) {
    if (taskData.intervalId) {
      clearInterval(taskData.intervalId);
    }
  }
  watchedTasks.clear();
};

function startTaskPoller(dispatch, getState, taskId, taskCategory, setRecognitionEvents) {
  const abortController = new AbortController();
  const signal = abortController.signal;

  const intervalId = setInterval(async () => {
    try {
      const response = await SEARCH_LONG_RUNNING_TASKS(TASK_QUERY, signal);
      const tasks = response?.entry?.map((e) => e.resource) || response?.resource || response || [];
      const serverTask = tasks.find(t => String(t.id) === String(taskId));

      if (!serverTask) {
        const meta = watchedTasks.get(taskId);
        if (meta && Date.now() - meta.addedOn > MAX_IDLE_MINUTES * 60 * 1000) {
          console.warn(`Task ${taskId} is stale, removing from watcher.`);
          dispatch(
            updateTask(taskId, {
              status: TASK_STATUS.FAILED,
              message: "Task timed out.",
            })
          );
          handleTaskCleanup(dispatch, getState, taskId, meta, TASK_STATUS.FAILED);
          clearInterval(intervalId);
          watchedTasks.delete(taskId);
        }
        return;
      }

      const meta = watchedTasks.get(taskId);
      const processor = TaskProcessorRegistry[taskCategory];
      const storeTask = getState().backgroundTasks.tasks.find(
        (t) => t.id === taskId
      );
      if (!processor || !storeTask) {
        console.error(`Missing processor or store task for ${taskId}`);
        clearInterval(intervalId);
        watchedTasks.delete(taskId);
        return;
      }

      const taskData = processor.parse(serverTask);

      const newStatus = taskData.status;
      let newTitle = "";

      const newProgress = calculateProgress(taskData, storeTask.progress);
      const newMessage = getTaskMessage(taskData, taskCategory);

      // Handle title updates for different task types
      if (taskCategory === "template") {
        const templateName = extractTemplateName(taskData);
        newTitle = storeTask.title;

        if (
          (templateName && !storeTask.title.includes(templateName)) ||
          newStatus === TASK_STATUS.COMPLETED
        ) {
          if (newStatus === TASK_STATUS.COMPLETED) {
            newTitle = `${templateName} Created Successfully`;
          } else {
            newTitle = `Creating ${templateName}`;
          }
        }
      } else if (taskCategory === "soap") {
        const soapTitle = extractSOAPNoteTitle(taskData);
        newTitle = storeTask.title;

        if (
          (soapTitle && !storeTask.title.includes(soapTitle)) ||
          newStatus === TASK_STATUS.COMPLETED
        ) {
          if (newStatus === TASK_STATUS.COMPLETED) {
            newTitle = `${soapTitle} Created Successfully`;
          } else {
            newTitle = `Creating ${soapTitle}`;
          }
        }
      }

      const updates = {
        ...taskData,
        status: newStatus,
        progress: newProgress,
        message: newMessage,
        title: newTitle,
        lastUpdated: Date.now(),
        isRead: false,
      };

      dispatch(updateTask(taskId, updates));

      // Handle SOAP task progress updates
      if (taskCategory === "soap") {
        const finalPayload = processor.extractFinalPayload(serverTask);
        let { soapPatientId, soapEncounterId } =
          extractPatientAndEncounterIdFromComposition(finalPayload);

        if (!soapPatientId || !soapEncounterId) {
          soapPatientId = serverTask?.for?.reference?.replace("Patient/", "");
          soapEncounterId = serverTask?.encounter?.reference?.replace(
            "Encounter/",
            ""
          );
        }

        // Update progress state during processing
        if (
          newStatus === TASK_STATUS.IN_PROGRESS ||
          newStatus === TASK_STATUS.QUEUED ||
          newStatus === TASK_STATUS.PENDING
        ) {
          dispatch(
            updateProgressState({
              patientId: soapPatientId,
              encounterId: soapEncounterId,
              isProgressReady: true,
              progress: Math.round(newProgress),
              progressMessage: newMessage,
              step: taskData.processedTaskGroups,
              totalSteps: taskData.totalTaskGroups,
            })
          );
        }

        // Handle task completion
        if (newStatus === TASK_STATUS.COMPLETED) {

          // Check if already processed to prevent duplicate handling
          if (storeTask.status === TASK_STATUS.COMPLETED) {
            clearInterval(intervalId);
            watchedTasks.delete(taskId);
            return;
          }

          const activeGenerations =
            getState().activeSOAPGeneration?.activeGenerations || [];
          const matchedGeneration = activeGenerations.find(
            (g) =>
              g.patientId === soapPatientId &&
              g.encounterId === soapEncounterId
          );
          const patientName = matchedGeneration?.patientName || "";

          dispatch(
            updateTask(taskId, {
              payload: {
                ...finalPayload,
                patientId: soapPatientId,
                encounterId: soapEncounterId,
              },
            })
          );

          const soapNoteTitle = finalPayload?.code?.text;
          const defaultClinicalNoteTitle = finalPayload?.title;

          handleSOAPTaskCompletion({
            dispatch,
            getState,
            setRecognitionEvents:
              typeof meta.setRecognitionEvents === "function"
                ? meta.setRecognitionEvents
                : () => { },
            soapPatientId,
            soapEncounterId,
            soapNoteTitle,
            defaultClinicalNoteTitle,
            practitionerId: storeTask.practitionerId,
            patientName: patientName,
            isSuccess: true,
          });

          handleTaskCleanup(
            dispatch,
            getState,
            taskId,
            meta,
            TASK_STATUS.COMPLETED,
            soapPatientId,
            soapEncounterId
          );
          clearInterval(intervalId);
          watchedTasks.delete(taskId);
        }

        // Handle task failure
        if (newStatus === TASK_STATUS.FAILED) {

          // Check if already processed to prevent duplicate handling
          if (storeTask.status === TASK_STATUS.FAILED) {
            clearInterval(intervalId);
            watchedTasks.delete(taskId);
            return;
          }

          toast.error(
            taskData?.error, {
            data: {
              ignoreNotification: true,
            },
          })

          handleTaskCleanup(
            dispatch,
            getState,
            taskId,
            meta,
            TASK_STATUS.FAILED,
            soapPatientId,
            soapEncounterId
          );
          clearInterval(intervalId);
          watchedTasks.delete(taskId);
        }
      }

      // Handle template task completion
      if (taskCategory === "template" && newStatus === TASK_STATUS.COMPLETED) {
        // Check if already processed to prevent duplicate toasts
        if (storeTask.status === TASK_STATUS.COMPLETED) {
          clearInterval(intervalId);
          watchedTasks.delete(taskId);
          return;
        }

        try {
          const finalPayload = processor.extractFinalPayload(serverTask);
          const templateName = extractTemplateName(taskData);
          dispatch(updateTask(taskId, { payload: finalPayload }));
          toast.success(`${templateName} generated successfully!`, {
            data: {
              ignoreNotification: true,
            },
          });
        } catch (err) {
          console.error(
            `Processor failed to extract final payload for ${taskId}:`,
            err
          );
          dispatch(
            updateTask(taskId, {
              status: TASK_STATUS.FAILED,
              message: "Failed to process result.",
            })
          );
          toast.error(
            `Something went wrong! Failed to generate your ${taskCategory}`,
            {
              data: {
                ignoreNotification: true,
              },
            }
          );
        }
        clearInterval(intervalId);
        watchedTasks.delete(taskId);
      }

      // Handle template task failure
      if (taskCategory === "template" && newStatus === TASK_STATUS.FAILED) {
        // Check if already processed
        if (storeTask.status === TASK_STATUS.FAILED) {
          console.log(`Template task ${taskId} already failed, skipping duplicate processing`);
          clearInterval(intervalId);
          watchedTasks.delete(taskId);
          return;
        }

        toast.error(
          taskData?.error, {
          data: {
            ignoreNotification: true,
          },
        }
        );
        clearInterval(intervalId);
        watchedTasks.delete(taskId);
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error(`Task polling failed for ${taskId}:`, err);

        const meta = watchedTasks.get(taskId);
        if (meta) {
          dispatch(
            updateTask(taskId, {
              status: TASK_STATUS.FAILED,
              message: "Failed to fetch task status.",
            })
          );
          handleTaskCleanup(dispatch, getState, taskId, meta, TASK_STATUS.FAILED);
        }

        clearInterval(intervalId);
        watchedTasks.delete(taskId);
      }
    }
  }, POLL_INTERVAL);

  return intervalId;
}

// Helper function to handle task cleanup
function handleTaskCleanup(
  dispatch,
  getState,
  taskId,
  meta,
  status,
  patientId = null,
  encounterId = null
) {
  if (meta.taskCategory === "soap") {
    if (!patientId || !encounterId) {
      const storeTask = getState().backgroundTasks.tasks.find(
        (t) => t.id === taskId
      );
      patientId = storeTask?.patientId;
      encounterId = storeTask?.encounterId;
    }

    if (patientId && encounterId) {
      dispatch(
        resetProgressState({
          patientId,
          encounterId,
        })
      );

      dispatch({
        type: constants.RECOGNIZER.STOP_RECORDING,
        payload: { patientId, encounterId },
      });

      dispatch({
        type: constants.RECOGNIZER.RESET_RECOGNIZER_FOR_ENCOUNTER,
      });

      dispatch(
        removeActiveSOAPGeneration({
          patientId,
          encounterId,
        })
      );

      refreshPatientHistoryWindow({
        setRecognitionEvents: meta.setRecognitionEvents,
        dispatch,
      });
    }
  }
}

export const restartInterruptedTasks = () => (dispatch, getState) => {
  console.log("Checking for interrupted tasks to restart...");

  const { tasks } = getState().backgroundTasks;

  const activeTasks = tasks.filter((task) =>
    [TASK_STATUS.IN_PROGRESS, TASK_STATUS.QUEUED].includes(task.status)
  );

  if (activeTasks.length > 0) {
    console.log(`Found ${activeTasks.length} active task(s) to re-watch.`);

    activeTasks.forEach((task) => {
      if (!watchedTasks.has(task.id)) {
        dispatch(
          startWatchingTask(
            task.id,
            task.task_category || "generic",
            () => { }
          )
        );
      }
    });
  }
};